import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface RevenueEventResponse {
  record_id: string;
  stripe_event_id: string;
  customer_id: string;
  product_slug: string;
  amount_usd_cents: number;
  amount_ils_agorot: number;
  vat_status: string;
  vat_amount_agorot: number;
  net_revenue_agorot: number;
  payment_type: string;
  customer_country: string;
  received_at: string;
}

interface ListRevenueEventsResponse {
  events: RevenueEventResponse[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function fmtIls(agorot: number): string { return '₪' + (agorot / 100).toFixed(2); }
export function fmtUsd(cents: number): string { return '$' + (cents / 100).toFixed(2); }
export function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function fmtVatStatus(s: string): string {
  return s === 'applicable' ? 'VAT liable' : s === 'exempt' ? 'Exempt' : s === 'reverse_charge' ? 'Reverse Charge' : s;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.06)',
  border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
  borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
  fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
};

function VatBadge({ status }: { status: string }) {
  const color =
    status === 'applicable'    ? '#fbbf24' :
    status === 'exempt'        ? '#6ee7b7' :
    status === 'reverse_charge' ? '#a78bfa' :
    'var(--omega-ink-subtle)';
  return (
    <span style={{
      fontSize: '7.5pt', padding: '1px 6px',
      border: `1px solid ${color}`, color,
      letterSpacing: '0.05em', fontWeight: 700,
      borderRadius: 3, fontFamily: 'var(--omega-mono)',
      whiteSpace: 'nowrap',
    }}>
      {fmtVatStatus(status)}
    </span>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function FinanceRevenueEventsPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const navigate = useNavigate();
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const [search, setSearch]     = useState('');

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (fromDate) p.set('from', fromDate);
    if (toDate)   p.set('to', toDate);
    return p.toString();
  }, [fromDate, toDate]);

  const { data, isPending, isError } = useQuery({
    queryKey: ['finance', 'revenue', 'events', fromDate, toDate],
    queryFn: () => apiFetch<ListRevenueEventsResponse>(`/finance/revenue/events${queryParams ? '?' + queryParams : ''}`),
    refetchInterval: 30_000,
  });

  const events = useMemo(() => {
    const all = data?.events ?? [];
    if (!search.trim()) return all;
    const q = search.trim().toLowerCase();
    return all.filter(e =>
      e.customer_id.toLowerCase().includes(q) ||
      e.product_slug.toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA', to: '/omega' },
        { label: 'Finance', to: '/omega/finance' },
        { label: 'Revenue', to: '/omega/finance/revenue' },
        { label: 'Events' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Revenue events
        </h1>
        {data && (
          <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, fontSize: '8pt', padding: '1px 7px', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {events.length}{events.length !== (data.total) ? `/${data.total}` : ''}
          </span>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <input
          type="date"
          value={fromDate}
          onChange={e => setFromDate(e.target.value)}
          style={inputStyle}
          placeholder="From-date"
        />
        <input
          type="date"
          value={toDate}
          onChange={e => setToDate(e.target.value)}
          style={inputStyle}
          placeholder="To-date"
        />
        <input
          placeholder="Search by customer / product…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 200, flex: '1 1 200px' }}
        />
        {(fromDate || toDate || search) && (
          <button
            onClick={() => { setFromDate(''); setToDate(''); setSearch(''); }}
            style={{ ...inputStyle, cursor: 'pointer', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
          >
            Clear
          </button>
        )}
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {/* Table header */}
      {!isPending && !isError && events.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: '140px 1fr 130px 90px 100px 100px 110px',
          gap: 10,
          padding: '6px 12px',
          borderBottom: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
          marginBottom: 2,
        }}>
          {['Receipt date', 'Customer', 'Product', 'Payment type', 'USD', 'ILS', 'VAT'].map(h => (
            <span key={h} style={{ fontSize: '8.5pt', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
              {h}
            </span>
          ))}
        </div>
      )}

      {!isPending && !isError && events.length === 0 && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic', padding: '40px 0', textAlign: 'center' }}>
          No events found.
        </div>
      )}

      {events.map(ev => (
        <div
          key={ev.record_id}
          onClick={() => navigate(`/omega/finance/revenue/events/${ev.record_id}`)}
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr 130px 90px 100px 100px 110px',
            gap: 10,
            alignItems: 'center',
            padding: '10px 12px',
            borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
            cursor: 'pointer',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(255,255,255,0.03)'; }}
          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
        >
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {fmtDate(ev.received_at)}
          </span>
          <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'var(--omega-mono)' }}>
            {ev.customer_id}
          </span>
          <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {ev.product_slug}
          </span>
          <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
            {ev.payment_type}
          </span>
          <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>
            {fmtUsd(ev.amount_usd_cents)}
          </span>
          <span style={{ fontSize: '9pt', color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)' }}>
            {fmtIls(ev.amount_ils_agorot)}
          </span>
          <VatBadge status={ev.vat_status} />
        </div>
      ))}
    </div>
  );
}
